import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const navigate = useNavigate();

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
    padding: '0 1rem'
  };
  
  const cardStyle = {
    maxWidth: '400px',
    width: '100%',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };
  
  const headingStyle = {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center'
  };
  
  const logoStyle = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#ff6b00',
    textAlign: 'center',
    marginBottom: '0.5rem'
  };
  
  const formStyle = {
    marginTop: '1.5rem'
  };
  
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    marginBottom: '1rem'
  };
  
  const buttonStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    fontWeight: '500',
    cursor: 'pointer'
  };
  
  const errorStyle = {
    color: '#dc2626',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };
  
  const linkStyle = {
    color: '#4f46e5',
    cursor: 'pointer',
    fontSize: '0.875rem'
  };

  useEffect(() => {
    // Check if already logged in
    if (authService.isAdminLoggedIn()) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await authService.adminLogin(email, password);
      
      if (response.data.success) {
        // Save token
        authService.saveAdminToken(response.data.token);
        
        // Save admin user data securely
        authService.storeAdminUser(response.data.admin);
        
        // Redirect to dashboard
        navigate('/');
      } else {
        setError(response.data.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await authService.requestPasswordReset(resetEmail, true); // true for admin
      
      if (response.data.success) {
        setResetSuccess(true);
      } else {
        setError(response.data.message || 'Password reset request failed.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.message || 'Password reset request failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={logoStyle}>CNXN</div>
        <h2 style={headingStyle}>
          {showResetForm ? 'Reset Admin Password' : 'Admin Login'}
        </h2>
        
        {!showResetForm ? (
          <form style={formStyle} onSubmit={handleLogin}>
            <div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                style={inputStyle}
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                style={inputStyle}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div style={errorStyle}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <button 
                type="button"
                style={linkStyle}
                onClick={() => setShowResetForm(true)}
              >
                Forgot your password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                style={buttonStyle}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        ) : (
          <div style={formStyle}>
            {resetSuccess ? (
              <div style={{ 
                backgroundColor: '#ecfdf5', 
                padding: '1rem', 
                borderRadius: '0.375rem',
                marginBottom: '1rem' 
              }}>
                <h3 style={{ 
                  fontWeight: '500', 
                  color: '#065f46',
                  marginBottom: '0.5rem'
                }}>
                  Password reset email sent
                </h3>
                <p style={{ color: '#047857', fontSize: '0.875rem' }}>
                  If an account exists with this email, you will receive a password reset link shortly.
                </p>
                <div style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    style={linkStyle}
                    onClick={() => {
                      setShowResetForm(false);
                      setResetSuccess(false);
                    }}
                  >
                    Return to login
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset}>
                <div>
                  <input
                    id="resetEmail"
                    name="resetEmail"
                    type="email"
                    autoComplete="email"
                    required
                    style={inputStyle}
                    placeholder="Email address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                </div>

                {error && (
                  <div style={errorStyle}>
                    {error}
                  </div>
                )}

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginTop: '1rem' 
                }}>
                  <button
                    type="button"
                    style={linkStyle}
                    onClick={() => setShowResetForm(false)}
                  >
                    Back to login
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      ...buttonStyle,
                      width: 'auto',
                      padding: '0.5rem 1rem'
                    }}
                  >
                    {isLoading ? 'Sending...' : 'Send reset link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
